/**
 * useFeatureFlags — exposes backend-driven feature flags from /api/auth/me.
 *
 * Backend retorna `feature_flags: { ztna_wizard_enabled: bool, ... }` no
 * payload de `/api/auth/me`. Este hook le do useAuthStore zustand (sem
 * refetch) e oferece helpers tipados.
 *
 * Default OFF — qualquer flag ausente ou nao "true" no env var do backend
 * vira false, garantindo prod intacto ate cutover (Lote 10).
 */
import { useAuthStore } from '@/store/auth'

export interface FeatureFlags {
  ztna_wizard_enabled: boolean
  rbac_template_caps_enabled: boolean
  finops_scope_resolver_enabled: boolean
  finops_dashboard_v2_enabled: boolean
  finops_reports_schedule_enabled: boolean
  aws_role_coverage_scan_enabled: boolean
  ztna_session_heartbeat_enabled: boolean
  inactivity_logout_enabled: boolean
  pwa_persistent_session: boolean
  /** Entrada Mailbox (spec 2026-10-19 §8): decidido por usuário no backend. */
  mailbox_ui_enabled: boolean
}

const DEFAULTS: FeatureFlags = {
  ztna_wizard_enabled: false,
  rbac_template_caps_enabled: false,
  finops_scope_resolver_enabled: false,
  finops_dashboard_v2_enabled: false,
  finops_reports_schedule_enabled: false,
  aws_role_coverage_scan_enabled: false,
  ztna_session_heartbeat_enabled: false,
  inactivity_logout_enabled: false,
  pwa_persistent_session: true,
  mailbox_ui_enabled: false,
}

export function useFeatureFlags(): FeatureFlags {
  const user = useAuthStore((s) => s.user)
  const raw = user?.feature_flags ?? {}
  return {
    ztna_wizard_enabled: raw.ztna_wizard_enabled === true,
    rbac_template_caps_enabled: raw.rbac_template_caps_enabled === true,
    finops_scope_resolver_enabled: raw.finops_scope_resolver_enabled === true,
    finops_dashboard_v2_enabled: raw.finops_dashboard_v2_enabled === true,
    finops_reports_schedule_enabled: raw.finops_reports_schedule_enabled === true,
    aws_role_coverage_scan_enabled: raw.aws_role_coverage_scan_enabled === true,
    ztna_session_heartbeat_enabled: raw.ztna_session_heartbeat_enabled === true,
    inactivity_logout_enabled: raw.inactivity_logout_enabled === true,
    // Default-ON: liga salvo se o backend mandar explicitamente false.
    pwa_persistent_session: raw.pwa_persistent_session !== false,
    mailbox_ui_enabled: raw.mailbox_ui_enabled === true,
  }
}

/** Convenience: read a single flag by key (default false). */
export function useFeatureFlag<K extends keyof FeatureFlags>(key: K): boolean {
  const flags = useFeatureFlags()
  return flags[key] ?? DEFAULTS[key] ?? false
}
